/**
 * Daily level lookup — resolves the level whose id matches `daily-<YYYY-MM-DD>`
 * for the user's local calendar date. Returns -1 when no matching level exists.
 */

import type { Completion, PuzzleDifficulty } from './types';
import { LEVEL_DATA } from './puzzles';
import { isLevelComplete, isSubPuzzleComplete } from './store';

function isoLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function targetDailyId(difficulty: PuzzleDifficulty, date: Date): string {
  const iso = isoLocalDate(date);
  return difficulty === 'advanced' ? `daily-${iso}-advanced` : `daily-${iso}`;
}

export function getDailyLevelIndex(
  difficulty: PuzzleDifficulty = 'casual',
  date: Date = new Date(),
): number {
  const pool = LEVEL_DATA.daily[difficulty];
  const targetId = targetDailyId(difficulty, date);
  return pool.findIndex((level) => level.id === targetId);
}

export function isDailyLevelComplete(
  completions: Record<string, Completion>,
  difficulty: PuzzleDifficulty = 'casual',
  date: Date = new Date(),
): boolean {
  const idx = getDailyLevelIndex(difficulty, date);
  if (idx < 0) return true;
  return isLevelComplete(completions, LEVEL_DATA.daily[difficulty][idx].id);
}

/** First not-yet-completed sub-puzzle index; -1 if all done. */
export function getDailyLevelFirstIncompleteSub(
  completions: Record<string, Completion>,
  difficulty: PuzzleDifficulty = 'casual',
  date: Date = new Date(),
): number {
  const idx = getDailyLevelIndex(difficulty, date);
  if (idx < 0) return -1;
  const level = LEVEL_DATA.daily[difficulty][idx];
  for (let i = 0; i < level.puzzles.length; i++) {
    if (!isSubPuzzleComplete(completions, level.id, level.puzzles[i].id)) {
      return i;
    }
  }
  return -1;
}
